import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  BATTALION_PULSE_POLL_MS,
  type BattalionPulseResponse,
  type BudgetVsActualRow,
  type DashboardSummary,
  type MonthlyFlowDashboardResponse,
  type PrivateSpotlight,
  type SubHeadSpendPoint,
} from '@cosvision/shared';
import {
  getBattalionPulse,
  getBudgetVsActual,
  getDashboardSummary,
  getFundSpotlight,
  getMonthlyFlow,
  getSubHeadSpend,
} from '../lib/api/dashboard.js';
import { qk } from '../lib/query-keys.js';

export function useDashboardSummaryQuery(
  opts: { enabled?: boolean } = {},
): UseQueryResult<DashboardSummary> {
  return useQuery({
    queryKey: qk.dashboard.summary(),
    queryFn: () => getDashboardSummary(),
    enabled: opts.enabled ?? true,
  });
}

export function useFundSpotlightQuery(fundCode: string): UseQueryResult<PrivateSpotlight> {
  return useQuery({
    queryKey: qk.dashboard.fundSpotlight(fundCode),
    queryFn: () => getFundSpotlight(fundCode),
    // Keep the previous fund's data on screen during a switch so the hero
    // never flashes a skeleton — this is core to the Aurora cross-fade.
    placeholderData: (prev) => prev,
  });
}

export function useMonthlyFlowQuery(
  fundId: string | null,
  months: number,
): UseQueryResult<MonthlyFlowDashboardResponse> {
  return useQuery({
    queryKey: qk.dashboard.monthlyFlow(fundId ?? '', months),
    queryFn: () => {
      if (!fundId) throw new Error('fundId is required');
      return getMonthlyFlow(fundId, months);
    },
    enabled: Boolean(fundId),
  });
}

export function useSubHeadSpendQuery(
  fundId: string | null,
  fyYear: number,
): UseQueryResult<SubHeadSpendPoint[]> {
  return useQuery({
    queryKey: qk.dashboard.subHeadSpend(fundId ?? '', fyYear),
    queryFn: () => {
      if (!fundId) throw new Error('fundId is required');
      return getSubHeadSpend(fundId, fyYear);
    },
    enabled: Boolean(fundId),
  });
}

export function useBudgetVsActualQuery(
  fundId: string | null,
  fyYear: number,
): UseQueryResult<BudgetVsActualRow[]> {
  return useQuery({
    queryKey: qk.dashboard.budgetVsActual(fundId ?? '', fyYear),
    queryFn: () => {
      if (!fundId) throw new Error('fundId is required');
      return getBudgetVsActual(fundId, fyYear);
    },
    enabled: Boolean(fundId),
  });
}

/**
 * The Battalion Pulse query — recent cross-board activity + "today" rollups.
 * Polls every 30s while the tab is focused. `refetchIntervalInBackground:
 * false` pauses polling on tab blur so an idle CO browser doesn't burn
 * requests overnight.
 */
export function useBattalionPulseQuery(
  opts: { enabled?: boolean } = {},
): UseQueryResult<BattalionPulseResponse> {
  return useQuery({
    queryKey: qk.dashboard.battalionPulse(),
    queryFn: () => getBattalionPulse(),
    enabled: opts.enabled ?? true,
    refetchInterval: BATTALION_PULSE_POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    // Match the server-side TtlCache so a refetch within the same window
    // doesn't hit the DB twice.
    staleTime: BATTALION_PULSE_POLL_MS - 1_000,
  });
}
